/**
 * AnalyticsSettings - Personalization Panel for Analytics Dashboards
 * 
 * This component provides user-adjustable settings for analytics dashboards:
 * - Date range filters
 * - Toggle metrics/charts on/off
 * - Change chart types
 * - Save preferences to localStorage
 */

import React, { useState, useEffect, useCallback } from 'react';

// Default settings for each dashboard type
const DEFAULT_SETTINGS = {
  dateRange: 'all', // all, week, month, quarter, year, custom
  customDateStart: null,
  customDateEnd: null,
  visibleMetrics: {}, // metric_id: boolean
  visibleCharts: {}, // chart_id: boolean
  chartTypes: {}, // chart_id: 'bar' | 'pie' | 'line' | 'donut'
  sortOrder: 'desc', // asc, desc
  sortBy: null, // column to sort by
};

// Storage key prefix
const STORAGE_KEY_PREFIX = 'analytics_settings_';

/**
 * Custom hook to manage analytics settings
 */
export const useAnalyticsSettings = (dashboardId, availableMetrics = [], availableCharts = []) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${dashboardId}`;
  
  // Initialize settings with defaults
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.warn('Failed to load analytics settings:', e);
    }
    
    // Create default visibility settings for metrics and charts
    const defaultMetrics = {};
    const defaultCharts = {};
    const defaultChartTypes = {};
    
    availableMetrics.forEach(m => {
      defaultMetrics[m.id] = m.defaultVisible !== false;
    });
    availableCharts.forEach(c => {
      defaultCharts[c.id] = c.defaultVisible !== false;
      defaultChartTypes[c.id] = c.defaultType || 'bar';
    });
    
    return {
      ...DEFAULT_SETTINGS,
      visibleMetrics: defaultMetrics,
      visibleCharts: defaultCharts,
      chartTypes: defaultChartTypes,
    };
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save analytics settings:', e);
    }
  }, [settings, storageKey]);

  // Update a single setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Toggle metric visibility
  const toggleMetric = useCallback((metricId) => {
    setSettings(prev => ({
      ...prev,
      visibleMetrics: {
        ...prev.visibleMetrics,
        [metricId]: !prev.visibleMetrics[metricId],
      },
    }));
  }, []);

  // Toggle chart visibility
  const toggleChart = useCallback((chartId) => {
    setSettings(prev => ({
      ...prev,
      visibleCharts: {
        ...prev.visibleCharts,
        [chartId]: !prev.visibleCharts[chartId],
      },
    }));
  }, []);

  // Change chart type
  const setChartType = useCallback((chartId, chartType) => {
    setSettings(prev => ({
      ...prev,
      chartTypes: {
        ...prev.chartTypes,
        [chartId]: chartType,
      },
    }));
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    const defaultMetrics = {};
    const defaultCharts = {};
    const defaultChartTypes = {};
    
    availableMetrics.forEach(m => {
      defaultMetrics[m.id] = m.defaultVisible !== false;
    });
    availableCharts.forEach(c => {
      defaultCharts[c.id] = c.defaultVisible !== false;
      defaultChartTypes[c.id] = c.defaultType || 'bar';
    });
    
    setSettings({
      ...DEFAULT_SETTINGS,
      visibleMetrics: defaultMetrics,
      visibleCharts: defaultCharts,
      chartTypes: defaultChartTypes,
    });
  }, [availableMetrics, availableCharts]);

  // Check if a metric is visible
  const isMetricVisible = useCallback((metricId) => {
    return settings.visibleMetrics[metricId] !== false;
  }, [settings.visibleMetrics]);

  // Check if a chart is visible
  const isChartVisible = useCallback((chartId) => {
    return settings.visibleCharts[chartId] !== false;
  }, [settings.visibleCharts]);

  // Get chart type
  const getChartType = useCallback((chartId, defaultType = 'bar') => {
    return settings.chartTypes[chartId] || defaultType;
  }, [settings.chartTypes]);

  // Filter data by date range
  const filterByDateRange = useCallback((data, dateField = 'date') => {
    if (settings.dateRange === 'all' || !dateField) return data;
    
    const now = new Date();
    let startDate;
    
    switch (settings.dateRange) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'quarter':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        startDate = settings.customDateStart ? new Date(settings.customDateStart) : null;
        const endDate = settings.customDateEnd ? new Date(settings.customDateEnd) : now;
        if (!startDate) return data;
        return data.filter(item => {
          const itemDate = new Date(item[dateField]);
          return itemDate >= startDate && itemDate <= endDate;
        });
      default:
        return data;
    }
    
    return data.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= startDate && itemDate <= now;
    });
  }, [settings.dateRange, settings.customDateStart, settings.customDateEnd]);

  return {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
    getChartType,
    filterByDateRange,
  };
};

/**
 * AnalyticsSettingsPanel - UI component for the settings panel
 */
const AnalyticsSettingsPanel = ({
  isOpen,
  onClose,
  settings,
  updateSetting,
  toggleMetric,
  toggleChart,
  setChartType,
  resetToDefaults,
  availableMetrics = [],
  availableCharts = [],
}) => {
  if (!isOpen) return null;

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'quarter', label: 'Last 90 Days' },
    { value: 'year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const chartTypeOptions = [
    { value: 'bar', label: 'Bar Chart', icon: '📊' },
    { value: 'pie', label: 'Pie Chart', icon: '🥧' },
    { value: 'line', label: 'Line Chart', icon: '📈' },
    { value: 'donut', label: 'Donut Chart', icon: '🍩' },
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '360px',
      backgroundColor: 'white',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: 10001,
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideIn 0.2s ease',
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f9fafb',
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#111827' }}>⚙️ Analytics Settings</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>Customize your dashboard view</p>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '6px 12px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
        {/* Date Range Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#374151', fontWeight: '600' }}>
            📅 Date Range
          </h4>
          <select
            value={settings.dateRange}
            onChange={(e) => updateSetting('dateRange', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            {dateRangeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
          {settings.dateRange === 'custom' && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <input
                type="date"
                value={settings.customDateStart || ''}
                onChange={(e) => updateSetting('customDateStart', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                }}
              />
              <input
                type="date"
                value={settings.customDateEnd || ''}
                onChange={(e) => updateSetting('customDateEnd', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                }}
              />
            </div>
          )}
        </div>

        {/* Visible Metrics Section */}
        {availableMetrics.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#374151', fontWeight: '600' }}>
              📊 Visible Metrics
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {availableMetrics.map(metric => (
                <label
                  key={metric.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#374151',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={settings.visibleMetrics[metric.id] !== false}
                    onChange={() => toggleMetric(metric.id)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>{metric.icon || '📈'}</span>
                  <span>{metric.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Visible Charts Section */}
        {availableCharts.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#374151', fontWeight: '600' }}>
              📉 Charts
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {availableCharts.map(chart => (
                <div
                  key={chart.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#374151',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={settings.visibleCharts[chart.id] !== false}
                      onChange={() => toggleChart(chart.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{chart.icon || '📊'}</span>
                    <span style={{ fontWeight: '500' }}>{chart.label}</span>
                  </label>
                  
                  {settings.visibleCharts[chart.id] !== false && chart.allowTypeChange !== false && (
                    <div style={{ marginLeft: '1.5rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {chartTypeOptions.map(type => (
                        <button
                          key={type.value}
                          onClick={() => setChartType(chart.id, type.value)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                            border: 'none',
                            backgroundColor: settings.chartTypes[chart.id] === type.value ? '#dc2626' : '#e5e7eb',
                            color: settings.chartTypes[chart.id] === type.value ? 'white' : '#374151',
                            cursor: 'pointer',
                          }}
                          title={type.label}
                        >
                          {type.icon}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '1rem 1.25rem',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
      }}>
        <button
          onClick={resetToDefaults}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#dc2626';
            e.target.style.color = 'white';
            e.target.style.borderColor = '#dc2626';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f3f4f6';
            e.target.style.color = '#374151';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          🔄 Reset to Defaults
        </button>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>
          Settings are saved automatically
        </p>
      </div>

      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}
      </style>
    </div>
  );
};

/**
 * SettingsButton - A small button to open the settings panel
 */
export const SettingsButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '0.5rem 1rem',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    }}
    title="Personalize Analytics"
  >
    ⚙️ Settings
  </button>
);

export default AnalyticsSettingsPanel;

